import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
// utils
import axiosInstance from '../utils/axios';

// ----------------------------------------------------------------------
// Which captive portal runs at the campus you are looking at.
//
// Deliberately shaped like useAccessProvider: same per-campus cache, same
// explicit campus_id, same rule that components branch on `capabilities` and
// never on `provider`. Vendors disagree about almost everything here. BhaiFi
// calls a site a NAS and can list its groups over the API; the next one will
// call it a location or a zone and may not list anything. `site_id_term`,
// `group_term` and `can_list_groups` are what the UI is supposed to read, so a
// new provider needs an adapter on the server and nothing here.
//
//   const { capabilities } = useCaptivePortalProvider();
//   if (capabilities.supports_session_kill) { ...show the Kill sessions button... }
// ----------------------------------------------------------------------

const PROVIDERS_ENDPOINT = '/api/v1/integrations/captive-portal/providers/';

const EMPTY_CAPABILITIES = {
  identity_kind: 'msisdn',
  identity_label: 'mobile number',
  native_time_windows: false,
  supports_groups: false,
  group_term: 'Group',
  site_id_term: 'Site ID',
  can_list_groups: false,
  supports_session_kill: false,
  supports_device_listing: false,
  supports_usage_stats: false,
  supports_policy_push: false,
  requires_name: false,
  requires_email: false,
  // The vendor cannot answer us inline: its appliance is on the customer's own
  // network and the work is queued for an agent there. Anything that reports a
  // result to an operator has to say "queued" rather than "done".
  deferred_execution: false,
};

const LOADING_STATE = {
  provider: null,
  label: null,
  capabilities: EMPTY_CAPABILITIES,
  providers: [],
  configuredProviders: [],
  enabled: false,
  loading: true,
  error: null,
};

// The registry is org-wide, but which provider is live is a campus question, so
// the cache is keyed by campus exactly as the door-access one is.
let cachedKey;
let cachedState = null;
let inFlight = null;
let inFlightKey;
const listeners = new Set();

function shape(data) {
  const providers = data?.providers || [];
  const configured = providers.filter((entry) => entry.configured);
  // An org running two vendors has no single answer, so the first configured
  // one is only a default for labels. Anything that must be right per site
  // reads the site's own provider off the site row instead.
  const live = configured[0] || null;

  return {
    provider: live?.slug || null,
    label: live?.label || null,
    capabilities: { ...EMPTY_CAPABILITIES, ...(live?.capabilities || {}) },
    providers,
    configuredProviders: configured.map((entry) => entry.slug),
    enabled: configured.length > 0,
    loading: false,
    error: null,
  };
}

function loadProviders(campusId, force) {
  const key = campusId || null;

  if (!force) {
    if (cachedState && cachedKey === key) return Promise.resolve(cachedState);
    if (inFlight && inFlightKey === key) return inFlight;
  }

  if (cachedKey !== key) {
    cachedState = null;
    cachedKey = key;
    listeners.forEach((notify) => notify(LOADING_STATE));
  }

  inFlightKey = key;
  inFlight = axiosInstance
    // Explicit, for the same reason as useAccessProvider: this answer must
    // describe the campus React just rendered for, not whatever localStorage
    // holds by the time the request goes out.
    .get(PROVIDERS_ENDPOINT, key ? { params: { campus_id: key } } : undefined)
    .then(({ data }) => shape(data))
    .catch((err) => ({
      ...LOADING_STATE,
      loading: false,
      error: err?.response?.data?.detail || 'Could not load captive portal providers',
    }))
    .then((next) => {
      if (inFlightKey !== key) return next;
      cachedState = next;
      cachedKey = key;
      inFlight = null;
      listeners.forEach((notify) => notify(next));
      return next;
    });

  return inFlight;
}

// Logout: the next user may be in a different org, and this module outlives the
// React tree because logout does not reload the page.
export function clearCaptivePortalProviderCache() {
  cachedState = null;
  cachedKey = undefined;
  inFlight = null;
  inFlightKey = undefined;
  listeners.forEach((notify) => notify(LOADING_STATE));
}

// After a vendor account is saved or removed on the Sites tab.
export function refreshCaptivePortalProvider() {
  return loadProviders(cachedKey ?? null, true);
}

export default function useCaptivePortalProvider() {
  const campusId = useSelector((state) => state.meta.activeCampus?.id || null);
  const [state, setState] = useState(() =>
    cachedState && cachedKey === campusId ? cachedState : LOADING_STATE
  );

  useEffect(() => {
    let alive = true;
    const notify = (next) => {
      if (alive) setState(next);
    };
    listeners.add(notify);
    loadProviders(campusId, false).then(notify);

    return () => {
      alive = false;
      listeners.delete(notify);
    };
  }, [campusId]);

  const refresh = useCallback(() => loadProviders(campusId, true), [campusId]);

  return { ...state, refresh };
}

// Capabilities for one site, which is what any per-site control should use.
// An org can run BhaiFi at one building and something else at another, so the
// org-level `capabilities` above is only a fallback for labels.
export function siteCapabilities(site, fallback = EMPTY_CAPABILITIES) {
  return { ...fallback, ...(site?.capabilities || {}) };
}

export { EMPTY_CAPABILITIES };
